/* eslint-disable @typescript-eslint/no-inferrable-types */
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { StyleClassModule } from 'primeng/styleclass';
import { DrawerModule } from 'primeng/drawer';
import { AvatarModule } from 'primeng/avatar';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { PopoverModule } from 'primeng/popover';
import { BehaviorSubject, Observable, of, Subject, shareReplay, catchError, finalize, takeUntil } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { ClientSubscriptionFreezeResponse, STAFFINTERFACE } from '@fitness-force/models';
import { FreezeService, TimeconversionService } from '@fitness-force/api';
import { COMPANY_INFO } from 'apps/ff-frontend/src/app/state/app.state';
import {  ShortNamePipe } from '@fitness-force/pipes/short-name'
import { CommmonPipeModule } from '@fitness-force/pipes/common-pipes'
import { DateTimeFormatPipe } from '@fitness-force/pipes/datetime-format';
import { FreezeEditComponent } from '../freeze-edit/freeze-edit.component';
import { FreezeCancelComponent } from '../freeze-cancel/freeze-cancel.component';
import { FreezeStopComponent } from '../freeze-stop/freeze-stop.component';
import { ClientSubscriptionFreezeStatus, PlanCategory } from '@fitness-force/enum';
import { StaffStoreService } from 'libs/api/src/lib/staff/staff-store.service';
@Component({
  selector: 'freeze-list',
  templateUrl: './freeze-list.component.html',
  styleUrls: ['./freeze-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    StyleClassModule,
    DrawerModule,
    AvatarModule,
    SelectModule,
    ButtonModule,
    TableModule,
    PopoverModule,
    CommmonPipeModule,
    ShortNamePipe,
    DateTimeFormatPipe,
    FreezeEditComponent,
    FreezeCancelComponent,
    FreezeStopComponent,
    TranslateModule
]
})
export class FreezeListComponent implements OnInit, OnDestroy {
  @Input('freeze') freeze$ = new BehaviorSubject<ClientSubscriptionFreezeResponse[]>([]);
  @Input('issidebarview') isSidebarView = false;

  @Output('isupdated') isUpdated: EventEmitter<boolean> = new EventEmitter();

  showOptions: boolean = false;

  editFreeze$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  selectedEditFreeze$ = new BehaviorSubject<ClientSubscriptionFreezeResponse | null>(null);

  cancelFreeze$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  selectedCancelFreeze$ = new BehaviorSubject<ClientSubscriptionFreezeResponse | null>(null);

  stopFreeze$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  selectedStopFreeze$ = new BehaviorSubject<ClientSubscriptionFreezeResponse | null>(null);

  loading$ = new BehaviorSubject(true);
  
  // Staff cache to prevent repeated API calls
  private staffCache: Map<number, Observable<STAFFINTERFACE>> = new Map();
  // Maximum cache size to prevent memory bloat
  private readonly MAX_CACHE_SIZE = 100;
  // Subject for unsubscribing on destroy
  private destroy$ = new Subject<void>();

  constructor(
    private freezeService: FreezeService,
    private translate: TranslateService,
    private store: Store,
    private timeService: TimeconversionService,
    private staffStoreService: StaffStoreService
  ) { }

  ngOnInit(): void {
    this.componentInit();
    
    // Listen to freeze changes to prefetch staff details
    this.freeze$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(freezes => {
      if (freezes && freezes.length > 0) {
        this.prefetchStaffDetails(freezes);
      }
    });
  }
  
  ngOnDestroy(): void {
    // Clean up resources
    this.destroy$.next();
    this.destroy$.complete();
    
    // Clear cache to free memory
    this.staffCache.clear();
  }

  componentInit() {
    this.store.select(COMPANY_INFO).subscribe((responseData) => {
      this.translate.setDefaultLang(responseData?.selectedLang?.LANG_CODE);
      this.translate.use(responseData?.selectedLang?.LANG_CODE);
    });
  }

  ConvertToLocalTimezone(utcDate: Date | string) {
    return this.timeService.ConvertUTCToLocalDateTime(utcDate);
  }

  /**
   * Get staff details with caching to prevent redundant API calls
   * @param staffId The ID of the staff member to retrieve
   * @returns Observable of staff details
   */
  GetStaff(staffId: number): Observable<STAFFINTERFACE> {
    if (!staffId) return of(null as unknown as STAFFINTERFACE);
    
    // Check cache first
    if (!this.staffCache.has(staffId)) {
      // If not in cache, fetch and store the result
      this.staffCache.set(
        staffId,
        this.staffStoreService.getStaffById(staffId).pipe(
          // Share the response with all subscribers to prevent multiple API calls
          shareReplay(1),
          // Handle errors gracefully
          catchError(() => of(null as unknown as STAFFINTERFACE)),
          // Ensure we don't exceed the max cache size
          finalize(() => this.limitCacheSize())
        )
      );
    }
    
    return this.staffCache.get(staffId) || of(null as unknown as STAFFINTERFACE);
  }
  
  /**
   * Prefetch staff details for all freeze entries
   * @param freezes List of freezes to prefetch staff details for
   */
  private prefetchStaffDetails(freezes: ClientSubscriptionFreezeResponse[]) {
    if (!freezes || freezes.length === 0) return;
    
    // Get unique staff IDs from all freezes
    const staffIds = new Set<number>();
    freezes.forEach(freeze => {
      if (freeze.updated_by && !this.staffCache.has(freeze.updated_by)) {
        staffIds.add(freeze.updated_by);
      }
    });
    
    // If no new staff details to fetch, return
    if (staffIds.size === 0) return;
    
    // Process staff IDs in batches to avoid overwhelming the API
    const batchSize = 10;
    const staffIdBatches = Array.from(staffIds).reduce((result, item, index) => { 
      const chunkIndex = Math.floor(index / batchSize);
      if (!result[chunkIndex]) result[chunkIndex] = [];
      result[chunkIndex].push(item);
      return result;
    }, [] as number[][]);
    
    // Process each batch
    staffIdBatches.forEach(batch => {
      // Create observables for each staff ID
      const requests = batch.map(id => {
        const request = this.staffStoreService.getStaffById(id).pipe(
          shareReplay(1),
          catchError(() => of(null as unknown as STAFFINTERFACE))
        );
        this.staffCache.set(id, request);
        return request;
      });
      
      // Subscribe to trigger the requests
      if (requests.length > 0) {
        // No need to store the result, just trigger the API calls
        // The results will be stored in the cache
        setTimeout(() => {
          requests.forEach(request => {
            request.pipe(takeUntil(this.destroy$)).subscribe();
          });
        }, 0);
      }
    });
  }
  
  /**
   * Limit the cache size to prevent memory bloat
   */
  private limitCacheSize() {
    if (this.staffCache.size > this.MAX_CACHE_SIZE) {
      // Get the oldest keys to remove
      const keysToDelete = Array.from(this.staffCache.keys()).slice(0, this.staffCache.size - this.MAX_CACHE_SIZE);
      keysToDelete.forEach(key => this.staffCache.delete(key));
    }
  }

  IsPayAsYouGo(subscriptionCategory: PlanCategory): boolean {
    return subscriptionCategory == PlanCategory.PayAsYouGo || subscriptionCategory.toString() == PlanCategory[PlanCategory.PayAsYouGo];
  }

  IsContract(freeze: ClientSubscriptionFreezeResponse): boolean {
    return freeze.client_subscription.category == PlanCategory.Contract || freeze.client_subscription.category.toString() == PlanCategory[PlanCategory.Contract]
    || freeze.client_subscription.category == PlanCategory.PayAsYouGo || freeze.client_subscription.category.toString() == PlanCategory[PlanCategory.PayAsYouGo];
  }

  //#region freeze edit
  ShowEditFreeze(freeze: ClientSubscriptionFreezeResponse): void {
    this.selectedEditFreeze$.next(freeze);
    this.editFreeze$.next(true);
  }

  HideEditFreeze(isUpdated: boolean): void {
    this.selectedEditFreeze$.next(null);
    this.editFreeze$.next(false);
    this.isUpdated.emit(isUpdated);
  }
  //#endregion freeze edit

  //#region freeze stop
  ShowStopFreeze(freeze: ClientSubscriptionFreezeResponse): void {
    this.selectedStopFreeze$.next(freeze);
    this.stopFreeze$.next(true);
  }

  

  HideStopFreeze(isUpdated: boolean): void {
    this.selectedStopFreeze$.next(null);
    this.stopFreeze$.next(false);
    this.isUpdated.emit(isUpdated);
  }
  //#endregion freeze stop

  //#region freeze cancel
  ShowCancelFreeze(freeze: ClientSubscriptionFreezeResponse): void {
    this.selectedCancelFreeze$.next(freeze);
    this.cancelFreeze$.next(true);
  }

  HideCancelFreeze(isUpdated: boolean): void {
    this.selectedCancelFreeze$.next(null);
    this.cancelFreeze$.next(false);
    this.isUpdated.emit(isUpdated);
  }
  //#endregion freeze cancel

  ShowEditOption(freeze: ClientSubscriptionFreezeResponse): boolean {
    return (freeze.status == ClientSubscriptionFreezeStatus.ScheduledFreeze || freeze.status?.toString() == ClientSubscriptionFreezeStatus[ClientSubscriptionFreezeStatus.ScheduledFreeze])
    || (freeze.status == ClientSubscriptionFreezeStatus.FreezeInprogress || freeze.status?.toString() == ClientSubscriptionFreezeStatus[ClientSubscriptionFreezeStatus.FreezeInprogress]);;
  }

  ShowCancelOption(freeze: ClientSubscriptionFreezeResponse): boolean {
    return freeze.status == ClientSubscriptionFreezeStatus.ScheduledFreeze || freeze.status?.toString() == ClientSubscriptionFreezeStatus[ClientSubscriptionFreezeStatus.ScheduledFreeze];
  }

  ShowStopOption(freeze: ClientSubscriptionFreezeResponse): boolean {
    return freeze.status == ClientSubscriptionFreezeStatus.FreezeInprogress || freeze.status?.toString() == ClientSubscriptionFreezeStatus[ClientSubscriptionFreezeStatus.FreezeInprogress];
  }
}
